import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CabPage from "../CabPage";
import "../css/fleet.css";
import useFleet, {
  STATUS_META,
  currencySign,
  plural,
  fmtMoney,
  fmtDate,
  agoText,
} from "../fleet/useFleet";
import { Ic, VehiclePhoto, StatusPill, MobileNav, Metric, METRIC_ICONS, Dialog, useToast, NoFleetAccess } from "../fleet/ui";
import ActiveOrder from "../fleet/ActiveOrder";
import { respondMatch, counterMatch, declineMatch, bulkStatus, bulkConfirm, viewMatch } from "../../utils/matchingApi";

const SORTERS = {
  vehicle: (v) => `${v.mark} ${v.model}`.toLowerCase(),
  plate: (v) => v.plate.toLowerCase(),
  dest: (v) => (v.destinations?.[0]?.city || "").toLowerCase(),
  rate: (v) => v.min_price ?? -1,
};

const DECIDED_TEXT = {
  responded: "Запрос принят — ожидайте ответа заказчика",
  countered: "Предложена цена",
  accepted: "Запрос принят",
  closed: "Запрос закрыт",
};

function RequestCard({ m, onAccept, onPrice, onDecline, busy }) {
  const l = m.load || {};
  const d = m.decided;
  const cargo = [l.body_type?.name, l.weight_t != null ? `${l.weight_t} т` : null, l.volume_m3 ? `${l.volume_m3} м³` : null]
    .filter(Boolean)
    .join(" · ");
  const loading = l.loading_from
    ? fmtDate(l.loading_from) + (l.loading_to && l.loading_to !== l.loading_from ? `–${fmtDate(l.loading_to)}` : "")
    : "—";
  return (
    <article className={`request-card ${d ? "done" : ""}`}>
      <div className="card-title">
        <strong>{l.customer || "Заказчик"}</strong>
        <time>{agoText(l.created_minutes_ago)}</time>
      </div>
      <div className="request-line">
        <Ic n="pin" />
        <span>{l.from} &nbsp;→&nbsp; {l.to}</span>
        <span className="fit">{m.score}% подходит</span>
      </div>
      {cargo && (
        <div className="request-line cargo-line">
          <Ic n="box" />
          {cargo}
        </div>
      )}
      <div className="request-line">
        <Ic n="calendar" />
        <span>Загрузка {loading}</span>
        <b>{l.price != null ? fmtMoney(l.price, l.currency) : "по договорённости"}</b>
      </div>
      {d ? (
        <div className="decision">
          {DECIDED_TEXT[d.state] || DECIDED_TEXT.closed}
          {d.state === "countered" && d.amount != null ? `: ${fmtMoney(d.amount, d.currency)}` : ""}
        </div>
      ) : (
        <div className="card-buttons">
          <button className="button primary" disabled={busy} onClick={() => onAccept(m)} type="button">Принять запрос</button>
          <button className="button" disabled={busy} onClick={() => onPrice(m)} type="button">Предложить цену</button>
          <button className="button danger" disabled={busy} onClick={() => onDecline(m)} type="button">
            <Ic n="trash" />
            Отклонить
          </button>
        </div>
      )}
    </article>
  );
}

export default function FleetPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { canFleet, loading, vehicles, requestsByVehicle, stats, liveError, error, load, refreshLive } = useFleet();
  const [toast, toastNode] = useToast();

  const [tab, setTab] = useState("requests"); // all | free | requests
  const [panelOpen, setPanelOpen] = useState(() => window.innerWidth >= 1200); // drawer covers the list on tablet/phone
  const [selId, setSelId] = useState(null);
  const [newestFirst, setNewestFirst] = useState(true);
  const [f, setF] = useState({ status: "all", body: "all", city: "all", dest: "all", query: "" });
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [checked, setChecked] = useState([]);
  const [dlg, setDlg] = useState(null); // {type: accept|price|decline, m}
  const [price, setPrice] = useState("");
  const [busyMatch, setBusyMatch] = useState(null);
  const [busyBulk, setBusyBulk] = useState(false);

  // legacy deep link «Создать транспорт» (/my-fleet?add=1) -> the full «Добавить машину» page
  useEffect(() => {
    if (params.get("add")) navigate("/my-fleet/add?new=1", { replace: true });
  }, [params, navigate]);

  // wireframe: Escape closes the dialog (Dialog handles it) or else the requests panel
  const dlgOpen = useRef(false);
  dlgOpen.current = !!dlg;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !dlgOpen.current) setPanelOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const setFilter = (patch) => {
    setF((p) => ({ ...p, ...patch }));
    setPage(1);
  };

  const bodyOptions = useMemo(() => {
    const m = new Map();
    vehicles.forEach((v) => v.body_type_id && m.set(v.body_type_id, v.body_type));
    return [...m.entries()];
  }, [vehicles]);
  const cityOptions = useMemo(() => [...new Set(vehicles.map((v) => v.city).filter(Boolean))], [vehicles]);
  const destOptions = useMemo(
    () => [...new Set(vehicles.flatMap((v) => (v.destinations || []).map((d) => d.city)))],
    [vehicles],
  );

  const filtered = useMemo(() => {
    const q = f.query.trim().toLowerCase();
    let list = vehicles.filter(
      (v) =>
        (tab !== "free" || v.state === "searching") &&
        (f.status === "all" || v.state === f.status) &&
        (f.body === "all" || String(v.body_type_id) === f.body) &&
        (f.city === "all" || v.city === f.city) &&
        (f.dest === "all" || (v.destinations || []).some((d) => d.city === f.dest)) &&
        (!q || `${v.mark} ${v.model} ${v.plate}`.toLowerCase().includes(q)),
    );
    if (sort.key) {
      const get = SORTERS[sort.key];
      list = [...list].sort((a, b) => {
        const x = get(a);
        const y = get(b);
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
      });
    }
    return list;
  }, [vehicles, f, tab, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, pages);
  const rows = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const selected =
    vehicles.find((v) => v.vehicle_id === selId) ||
    vehicles.find((v) => (requestsByVehicle.get(v.vehicle_id) || []).some((m) => !m.decided)) ||
    vehicles[0] ||
    null;
  const selRequests = selected ? requestsByVehicle.get(selected.vehicle_id) || [] : [];
  const orderedRequests = newestFirst ? selRequests : [...selRequests].reverse();
  const pendingSel = selRequests.filter((m) => !m.decided).length;

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const resetFilters = () => {
    setF({ status: "all", body: "all", city: "all", dest: "all", query: "" });
    setTab((t) => (t === "free" ? "all" : t));
    setPage(1);
  };

  const pickTab = (t) => {
    setTab(t);
    setPage(1);
    setPanelOpen(t === "requests");
  };
  const openVehicle = (id) => {
    // opening a truck's requests = the carrier has seen them (feeds «просмотрено» on the load side)
    const fresh = (requestsByVehicle.get(id) || []).filter((m) => !m.decided && !m.viewed);
    if (fresh.length) Promise.allSettled(fresh.map((m) => viewMatch(m.match_id))).then(() => refreshLive(true));
    setSelId(id);
    setPanelOpen(true);
    if (tab !== "requests") setTab("requests");
  };

  const act = async (fn, okText, errText) => {
    setBusyMatch(dlg?.m?.match_id || null);
    try {
      await fn();
      toast(okText);
      setDlg(null);
      await refreshLive(true);
    } catch (e) {
      toast(e?.message || errText);
    } finally {
      setBusyMatch(null);
    }
  };

  const bulk = async (fn, okText) => {
    setBusyBulk(true);
    try {
      const res = await fn();
      toast(okText(res));
      setChecked([]);
      load();
    } catch (e) {
      toast(e?.message || "Ошибка");
    } finally {
      setBusyBulk(false);
    }
  };

  const allOnPage = rows.length > 0 && rows.every((v) => checked.includes(v.vehicle_id));
  const toggleAll = () =>
    setChecked((c) =>
      allOnPage ? c.filter((id) => !rows.some((v) => v.vehicle_id === id)) : [...new Set([...c, ...rows.map((v) => v.vehicle_id)])],
    );

  const sortMark = (key) => (sort.key === key ? (sort.dir === 1 ? " ↑" : " ↓") : " ↕");
  const th = (key, text) => (
    <th onClick={() => toggleSort(key)} style={{ cursor: "pointer" }}>
      {text}
      {sortMark(key)}
    </th>
  );

  const pendingTitle = pendingSel
    ? `${pendingSel} ${plural(pendingSel, "новый запрос", "новых запроса", "новых запросов")}`
    : "Нет новых запросов";

  if (!canFleet) {
    return (
      <CabPage name="fleet">
        <NoFleetAccess />
        <MobileNav />
      </CabPage>
    );
  }

  return (
    <CabPage name="fleet">
      <style>{`@media(max-width:900px){.cab-fleet .add-actions{width:100%;margin-left:0!important}.cab-fleet .add-actions .add-vehicle{flex:1 1 100%;justify-content:center}}`}</style>
      <div className={panelOpen ? "" : "panel-closed"}>
        <main id="main">
          <section className="intro">
            <h1>Мой транспорт</h1>
            <p>Управляйте своим автопарком. Отмечайте свободные машины, получайте заказы и отслеживайте рейсы.</p>
          </section>

          {(liveError || error) && (
            <p style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "#fff5e7", color: "#b45f06", fontSize: 13 }}>
              {error || "Сервис подбора недоступен — статусы и запросы могут быть неактуальны."}
            </p>
          )}

          <section className="fleet-metrics">
            <Metric idx={0} icon={METRIC_ICONS.total} value={stats.total} label="Всего машин" small="В автопарке" />
            <Metric idx={1} icon={METRIC_ICONS.free} value={stats.active} label="Ищут груз сейчас" />
            <Metric idx={2} icon={METRIC_ICONS.tomorrow} value={stats.tomorrow} label="Освобождаются завтра" />
            <Metric idx={3} icon={METRIC_ICONS.trip} value={stats.busy} label="В рейсе" />
            <Metric idx={4} icon={METRIC_ICONS.requests} value={stats.newRequests} label="Новые запросы" small={stats.newRequests > 0 ? "требуют ответа" : null} />
          </section>

          <div className="fleet-tabs">
            <div aria-label="Раздел автопарка" role="tablist">
              <button aria-selected={tab === "all"} className={tab === "all" ? "active" : ""} onClick={() => pickTab("all")} role="tab" type="button">
                Автопарк
              </button>
              <button aria-selected={tab === "free"} className={tab === "free" ? "active" : ""} onClick={() => pickTab("free")} role="tab" type="button">
                Свободные машины <span>{stats.active}</span>
              </button>
              <button aria-selected={tab === "requests"} className={tab === "requests" ? "active" : ""} onClick={() => pickTab("requests")} role="tab" type="button">
                Запросы <span>{stats.newRequests}</span>
              </button>
            </div>
            <div className="add-actions" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start", marginLeft: "auto" }}>
              <button className="button primary add-vehicle" data-action="add" onClick={() => navigate("/my-fleet/add?new=1")} style={{ minWidth: 0, marginLeft: 0 }} type="button">
                <Ic n="plus" />
                <span>Добавить машину</span>
              </button>
            </div>
          </div>

          <form id="fleet-filters" onSubmit={(e) => e.preventDefault()}>
            <label className="field">
              <span>Статус</span>
              <span className="select-wrap">
                <select aria-label="Статус" onChange={(e) => setFilter({ status: e.target.value })} value={f.status}>
                  <option value="all">Все статусы</option>
                  <option value="searching">Ищет груз</option>
                  <option value="trip">В рейсе</option>
                  <option value="tomorrow">Освобождается завтра</option>
                  <option value="idle">Стоит</option>
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Тип кузова</span>
              <span className="select-wrap">
                <select aria-label="Тип кузова" onChange={(e) => setFilter({ body: e.target.value })} value={f.body}>
                  <option value="all">Все типы</option>
                  {bodyOptions.map(([id, name]) => (
                    <option key={id} value={String(id)}>{name}</option>
                  ))}
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Город (где сейчас)</span>
              <span className="select-wrap">
                <select aria-label="Город (где сейчас)" onChange={(e) => setFilter({ city: e.target.value })} value={f.city}>
                  <option value="all">Все города</option>
                  {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Куда ищет</span>
              <span className="select-wrap">
                <select aria-label="Куда ищет" onChange={(e) => setFilter({ dest: e.target.value })} value={f.dest}>
                  <option value="all">Все направления</option>
                  {destOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Ic n="chevron" />
              </span>
            </label>
            <label className="fleet-search">
              <Ic n="search" />
              <input
                aria-label="Поиск по госномеру или марке"
                onChange={(e) => setFilter({ query: e.target.value })}
                placeholder="Поиск по госномеру, марке..."
                value={f.query}
              />
            </label>
          </form>

          {checked.length > 0 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "0 0 12px", padding: "8px 12px", background: "#ecf4ff", borderRadius: 8 }}>
              <b style={{ fontSize: 13 }}>Выбрано: {checked.length}</b>
              <button className="button primary" onClick={() => navigate(`/my-fleet/add?vehicles=${checked.join(",")}`)} style={{ minHeight: 32, padding: "4px 12px" }} type="button">
                Обновить состояние
              </button>
              <button className="button" disabled={busyBulk} onClick={() => bulk(() => bulkStatus(checked, "BUSY"), (r) => `Переведено в рейс: ${r.updated}`)} style={{ minHeight: 32, padding: "4px 12px" }} type="button">
                В рейсе
              </button>
              <button className="button" disabled={busyBulk} onClick={() => bulk(() => bulkStatus(checked, "INACTIVE"), (r) => `Снято с поиска: ${r.updated}`)} style={{ minHeight: 32, padding: "4px 12px" }} type="button">
                Неактивна
              </button>
              <button className="button" disabled={busyBulk} onClick={() => bulk(() => bulkConfirm(checked), (r) => `Подтверждено машин: ${r.confirmed}`)} style={{ minHeight: 32, padding: "4px 12px" }} type="button">
                Подтвердить всё
              </button>
              <button onClick={() => setChecked([])} style={{ fontSize: 12, color: "#536b9c" }} type="button">Снять</button>
            </div>
          )}

          <div className="table-container">
            <table>
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "17.2%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "8.6%" }} />
                <col style={{ width: "11.3%" }} />
                <col style={{ width: "11.6%" }} />
                <col style={{ width: "10.8%" }} />
                <col style={{ width: "13.5%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "4%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th><input aria-label="Выбрать все машины" checked={allOnPage} id="check-all" onChange={toggleAll} type="checkbox" /></th>
                  {th("vehicle", "Машина")}
                  {th("plate", "Госномер")}
                  <th>Тип кузова</th>
                  <th>Где сейчас</th>
                  {th("dest", "Куда ищет груз")}
                  {th("rate", "Ставка от")}
                  <th>Статус</th>
                  <th>Запросы</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => {
                  const list = requestsByVehicle.get(v.vehicle_id) || [];
                  const fresh = list.filter((m) => !m.decided).length;
                  const dests = v.destinations || [];
                  const title = [v.mark, v.model && v.model !== v.mark ? v.model : ""].filter(Boolean).join(" ");
                  return (
                    <tr className={selected?.vehicle_id === v.vehicle_id ? "selected" : ""} key={v.vehicle_id}>
                      <td><input aria-label={`Выбрать ${title}`} checked={checked.includes(v.vehicle_id)} className="row-check" onChange={() => setChecked((c) => (c.includes(v.vehicle_id) ? c.filter((x) => x !== v.vehicle_id) : [...c, v.vehicle_id]))} type="checkbox" /></td>
                      <td data-label="Машина">
                        <button className="vehicle" onClick={() => openVehicle(v.vehicle_id)} type="button">
                          <VehiclePhoto v={v} />
                          <span>
                            <strong>{title}</strong>
                            <small>{v.body_type} • {v.max_load_weight != null ? `${v.max_load_weight} т` : "—"}</small>
                          </span>
                        </button>
                      </td>
                      <td data-label="Госномер"><span className="plate">{v.plate}</span></td>
                      <td data-label="Тип кузова">{v.body_type}<small>{v.max_load_weight != null ? `${v.max_load_weight} тонн` : ""}</small></td>
                      <td data-label="Где сейчас">
                        {v.city ? <span className="city"><Ic n="pin" />{v.city}</span> : "—"}
                      </td>
                      <td data-label="Куда ищет груз">
                        {dests.length > 0 ? (
                          <>
                            {dests.map((d) => d.city).join(", ")}
                            <small>{dests[0].country || ""}</small>
                          </>
                        ) : v.live && v.state !== "idle" ? "Любое" : "—"}
                      </td>
                      <td data-label="Ставка от">
                        {v.min_price != null ? <b>от {Number(v.min_price).toLocaleString("ru-RU")} {currencySign(v.currency)}</b> : <b>—</b>}
                      </td>
                      <td data-label="Статус"><StatusPill meta={STATUS_META} state={v.state} /></td>
                      <td data-label="Запросы">
                        <button className={`request-count${fresh ? " has-requests" : ""}`} onClick={() => openVehicle(v.vehicle_id)} type="button">
                          {fresh ? `${fresh} ${plural(fresh, "новый", "новых", "новых")}` : "—"}
                        </button>
                      </td>
                      <td>
                        <button aria-label={`Открыть запросы ${title}`} className="row-open" onClick={() => openVehicle(v.vehicle_id)} type="button">›</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!loading && rows.length === 0 && (
              <div className="empty">
                <h3>{vehicles.length === 0 ? "В автопарке пока нет машин" : "Машины не найдены"}</h3>
                <p>{vehicles.length === 0 ? "Добавьте первую машину — укажите данные и доступность, и вы начнёте получать запросы от грузоотправителей." : "Измените фильтры поиска."}</p>
                {vehicles.length === 0 ? (
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <button className="button primary" onClick={() => navigate("/my-fleet/add?new=1")} type="button">Добавить первую машину</button>
                  </div>
                ) : (
                  <button className="button" onClick={resetFilters} type="button">Сбросить</button>
                )}
              </div>
            )}
          </div>

          <footer className="table-footer">
            <span id="fleet-count">
              Показано {rows.length} из {filtered.length} {plural(filtered.length, "машины", "машин", "машин")}
            </span>
            <div>
              <select aria-label="Количество машин на странице" id="page-size" onChange={(e) => { setPageSize(+e.target.value); setPage(1); }} value={pageSize}>
                <option value={10}>10 на странице</option>
                <option value={20}>20 на странице</option>
              </select>
              <button aria-label="Предыдущая страница" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} type="button">‹</button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button className={p === curPage ? "active" : ""} key={p} onClick={() => setPage(p)} type="button">{p}</button>
              ))}
              <button aria-label="Следующая страница" disabled={curPage >= pages} onClick={() => setPage(curPage + 1)} type="button">›</button>
            </div>
          </footer>
        </main>

        <aside aria-label="Входящие запросы" className="requests-panel">
          <header>
            <h2>Входящие запросы</h2>
            <button aria-label="Закрыть входящие запросы" className="icon-button" onClick={() => setPanelOpen(false)} type="button">
              <Ic n="close" />
            </button>
          </header>
          {selected ? (
            <>
              <div className="selected-vehicle">
                <VehiclePhoto className="drawer-truck" v={selected} />
                <div>
                  <strong>{[selected.mark, selected.model && selected.model !== selected.mark ? selected.model : ""].filter(Boolean).join(" ")}</strong>
                  <small>
                    {selected.body_type} • {selected.max_load_weight != null ? `${selected.max_load_weight} тонн` : "—"} • <span>{selected.plate}</span>
                  </small>
                </div>
                <StatusPill meta={STATUS_META} state={selected.state} />
              </div>
              <div style={{ display: "flex", gap: 8, margin: "10px 0 0" }}>
                <button className="button" onClick={() => navigate(`/my-fleet/add?vehicle=${selected.vehicle_id}`)} style={{ flex: 1, minHeight: 34, fontSize: 12 }} type="button">
                  Изменить машину
                </button>
              </div>
              {selected.state === "trip" && <ActiveOrder vehicleId={selected.vehicle_id} />}
              <div className="requests-heading">
                <strong>{pendingTitle}</strong>
                <button onClick={() => setNewestFirst((v) => !v)} type="button">{newestFirst ? "Сначала новые ↕" : "Сначала ранние ↕"}</button>
              </div>
              <div id="request-cards">
                {orderedRequests.length ? (
                  orderedRequests.map((m) => (
                    <RequestCard
                      busy={busyMatch === m.match_id}
                      key={m.match_id}
                      m={m}
                      onAccept={(x) => setDlg({ type: "accept", m: x })}
                      onDecline={(x) => setDlg({ type: "decline", m: x })}
                      onPrice={(x) => {
                        setPrice(x.load?.price != null ? String(x.load.price) : "");
                        setDlg({ type: "price", m: x });
                      }}
                    />
                  ))
                ) : (
                  <div className="drawer-empty">
                    <h3>Запросов пока нет</h3>
                    <p>Здесь появятся обращения по выбранной машине.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="drawer-empty">
              <h3>Машин пока нет</h3>
              <p>Добавьте машину, чтобы получать запросы по грузам.</p>
              <button className="button primary" onClick={() => navigate("/my-fleet/add?new=1")} style={{ marginTop: 12 }} type="button">Добавить машину</button>
            </div>
          )}
        </aside>
      </div>

      <MobileNav />

      {dlg?.type === "accept" && (
        <Dialog onClose={() => setDlg(null)} title="Принять запрос">
          <p>{dlg.m.load.customer || "Заказчик"} · {dlg.m.load.from} → {dlg.m.load.to} · {fmtMoney(dlg.m.load.price, dlg.m.load.currency)}</p>
          <div className="actions">
            <button className="button" onClick={() => setDlg(null)} type="button">Отмена</button>
            <button className="button primary" disabled={busyMatch != null} onClick={() => act(() => respondMatch(dlg.m.match_id), "Запрос принят — ожидайте ответа заказчика", "Не удалось принять запрос")} type="button">Подтвердить</button>
          </div>
        </Dialog>
      )}
      {dlg?.type === "decline" && (
        <Dialog onClose={() => setDlg(null)} title="Отклонить запрос">
          <p>Подтвердите отклонение запроса {dlg.m.load.from} → {dlg.m.load.to}.</p>
          <div className="actions">
            <button className="button" onClick={() => setDlg(null)} type="button">Отмена</button>
            <button className="button" disabled={busyMatch != null} onClick={() => act(() => declineMatch(dlg.m.match_id), "Запрос отклонён", "Не удалось отклонить")} type="button">Отклонить запрос</button>
          </div>
        </Dialog>
      )}
      {dlg?.type === "price" && (
        <Dialog onClose={() => setDlg(null)} title="Предложить цену">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const amount = Number(price);
              if (!(amount > 0)) return toast("Укажите ставку");
              act(() => counterMatch(dlg.m.match_id, amount), "Цена предложена", "Не удалось предложить цену");
            }}
          >
            {dlg.m.load.price != null && <p>Ставка заказчика: {fmtMoney(dlg.m.load.price, dlg.m.load.currency)}</p>}
            <label>
              Ваша ставка{dlg.m.load.currency ? `, ${dlg.m.load.currency}` : ""}
              <input autoFocus min="1" onChange={(e) => setPrice(e.target.value)} required step="any" type="number" value={price} />
            </label>
            <div className="actions">
              <button className="button" onClick={() => setDlg(null)} type="button">Отмена</button>
              <button className="button primary" disabled={busyMatch != null} type="submit">Сохранить предложение</button>
            </div>
          </form>
        </Dialog>
      )}
      {toastNode}
    </CabPage>
  );
}
